<script lang="ts" setup>
import { computed, type PropType } from 'vue';
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
} from '@headlessui/vue';

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: '',
  },

  options: {
    type: Array as PropType<Record<string, any>[]>,
    default: () => [],
  },

  valueKey: {
    type: String,
    default: 'value',
  },

  labelKey: {
    type: String,
    default: 'label',
  },
});

const emit = defineEmits(['update:modelValue']);

const listboxValue = computed({
  get() {
    return props.options.find((item) => item[props.valueKey] === props.modelValue) ?? null;
  },

  set(value) {
    emit('update:modelValue', value?.[props.valueKey] ?? '');
  },
});
</script>

<template>
  <Listbox v-model="listboxValue">
    <div class="component-listbox relative">
      <div class="gray-border box-border h-40px">
        <ListboxButton class="h-full w-full flex cursor-pointer items-center justify-between rounded-4px border-none bg-white p-0 text-hex-2d2d2d" v-slot="{ open }">
          <div class="flex-grow truncate px-16px text-left">{{ listboxValue?.[props.labelKey] ?? '' }}</div>
          <div class="i-home-icons:arrow-down mr-16px h-16px w-16px transition-transform" :class="{ 'rotate-180': open }"></div>
        </ListboxButton>
      </div>

      <Transition
        leave-active-class="transition duration-100 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <ListboxOptions class="gray-border absolute z-1 mb-0 mt-4px box-border max-h-500px w-full list-none overflow-auto bg-white px-0 py-8px">
          <ListboxOption
            v-for="item in props.options"
            as="template"
            :key="item[props.valueKey]"
            :value="item"
            v-slot="{ selected, active }"
          >
            <li
              class="cursor-pointer select-none px-16px leading-40px"
              :class="{
                'text-brand-primary': active || selected,
                'text-hex-2d2d2d': !active && !selected,
              }"
            >
              <div
                class="inline-block whitespace-nowrap pr-8px"
                :class="{
                  'font-medium': selected,
                  'font-normal': !selected,
                }"
              >
                {{ item[props.labelKey] }}
              </div>
            </li>
          </ListboxOption>
        </ListboxOptions>
      </Transition>
    </div>
  </Listbox>
</template>

<style lang="scss" scoped>
.component-listbox {
  .gray-border {
    border: 1px solid rgba(154, 158, 166, 0.2);
    border-radius: 4px;
    outline-style: none;
  }
}
</style>
